import React, { useContext, useLayoutEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import ButtonGroup from '@swf/components/ButtonGroup/ButtonGroup';
import createForm from '@swf/components/Forms/Form';
import Text from '@swf/components/Inputs/Text';
import Button from '@swf/components/StarsButton/StarsButton';
import StarsInfo from '@swf/components/StarsInfo/StarsInfo';
import { ModalContext } from '@swf/components/StarsModal/Context';
import Body from '@swf/components/StarsModal/components/Body';
import Translation from '@swf/components/Translation/Translation';
import useRequest from '@swf/core/hooks/useRequest';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { numbersOnly, required } from '@swf/core/utils/validators';
import { useHomeGamesWidget } from '@swf/poker';

import Header from '../../Modal/Header/Header';

import {
  TXTCLI_Club_ID,
  TXTCLI_Invitation_Code,
  TXTCLI_Join_Club,
  TXTCLI_Join_a_club,
  TXTCLI_XbXPrivacy_NoteXX_bX_Your_name_ELL,
  TXTCLI_Your_Name,
  TXTCLI_Your_identity_to_all_club_memb_ELL,
  TXTWCL_Whole_numbers_onlyX_No_decimal_ELL
} from './JoinClub.messages.json';
import styles from './JoinClub.scss';

interface FormValues {
  clubId: string;
  invitationCode: string;
  yourName: string;
}

const Form = createForm<FormValues>();
interface JoinResponse {
  error?: string | null;
  message?: string;
}
const initialJoinResponse: JoinResponse = { error: '', message: '' };

interface LocationState {
  clubId: string;
  invitationCode: string;
  widgetId?: string;
}
export interface AddUserClubReply {
  body: {
    message: string;
    success: boolean;
    addUserClubReply: {
      clubName: string | null;
      errCode: number;
      errDesc: string | null;
      socialToken: string | null;
    };
  };
}

export const JoinClub: React.FC = () => {
  const userId = useSelector(select.account.getUserId);
  const [joinResponse, setJoinResponse] = useState(initialJoinResponse);
  const request = useRequest();
  const { formatMessage } = useIntl();
  const { modal } = useDispatch<Dispatch>();
  const {
    state: { clubId: inviteClubId, invitationCode: inviteInvitationCode, widgetId }
  } = useLocation<LocationState>();
  const { widgetDispatch } = useHomeGamesWidget(widgetId);
  const { setStyle } = useContext(ModalContext);

  useLayoutEffect(() => {
    setStyle({ className: styles.joinClubModal, closeOnOverlayClick: true, closeOnEsc: true });
  });

  const handleSubmit = ({ clubId, invitationCode, yourName }: FormValues) => {
    request({
      service: 'homeGames.addUserClubRequest',
      body: {
        addUserClub: {
          clubId,
          clubPassword: invitationCode,
          comment: yourName ?? ''
        }
      }
    }).subscribe(({ body: { message, success, addUserClubReply } }: AddUserClubReply) => {
      if (!success) return;

      setJoinResponse({ message, error: addUserClubReply.errDesc });

      if (!addUserClubReply.errDesc && message) {
        const timeOut = setTimeout(async () => {
          modal.close();

          await widgetDispatch.getUserClubs();
          clearTimeout(timeOut);
        }, 250);
      }
    });
  };
  const { error } = joinResponse;

  const clubIDText = formatMessage(TXTCLI_Club_ID);
  const invitationCodeText = formatMessage(TXTCLI_Invitation_Code);
  const yourNameText = formatMessage(TXTCLI_Your_Name);

  return (
    <>
      <Header title={<Translation {...TXTCLI_Join_a_club} />} />
      <Body className={styles.body}>
        <div className={styles.joinClub} data-testid="join-club-modal">
          <Form
            onSubmit={handleSubmit}
            initialValues={{
              clubId: inviteClubId,
              invitationCode: inviteInvitationCode,
              yourName: ''
            }}
            render={({ form }) => {
              const { submitting, validating, valid } = form.getState();

              return (
                <>
                  <Text
                    className={styles.input}
                    id="clubId"
                    label={clubIDText}
                    showValidationTick={false}
                    placeholder={clubIDText}
                    validators={[
                      required(),
                      numbersOnly(TXTWCL_Whole_numbers_onlyX_No_decimal_ELL)
                    ]}
                  />
                  <Text
                    className={styles.input}
                    id="invitationCode"
                    label={invitationCodeText}
                    showValidationTick={false}
                    placeholder={invitationCodeText}
                    validators={[required()]}
                  />
                  <Text
                    className={styles.input}
                    id="yourName"
                    label={yourNameText}
                    showValidationTick={false}
                    placeholder={yourNameText}
                  />
                  <div className={styles.yourIdentity}>
                    <Translation tagName="p" {...TXTCLI_XbXPrivacy_NoteXX_bX_Your_name_ELL} />
                    <Translation
                      tagName="p"
                      values={{ 0: userId }}
                      {...TXTCLI_Your_identity_to_all_club_memb_ELL}
                    />
                  </div>
                  {error && (
                    <StarsInfo className={styles.errorPanel} type="error">
                      {error}
                    </StarsInfo>
                  )}
                  <ButtonGroup>
                    <Button
                      id="joinClubSubmit"
                      className={styles.submit}
                      type="submit"
                      loading={submitting}
                      disabled={!valid || validating}
                      translation={TXTCLI_Join_Club}
                    />
                  </ButtonGroup>
                </>
              );
            }}
          />
        </div>
      </Body>
    </>
  );
};

export default JoinClub;
